<template>
  <div class="as-page">
    <AsPageHeader
      breadcrumb="Personal > Consulta"
      kicker="LEGAJO RRHH"
      title="Consulta de legajo"
      subtitle="Seleccione al colaborador por DNI, código o apellidos y consulte la ficha en solo lectura."
    />

    <v-alert type="info" dense outlined class="as-alert">
      Perfil activo: <strong>{{ perfilLabel }}</strong>.
      <span v-if="perfilActivo === 'administrador'">Consulta completa de la ficha.</span>
      <span v-else-if="perfilActivo === 'legajo'">Solo campos de Legajo.</span>
      <span v-else>Solo campos de Planilla.</span>
      <span v-if="!puedeVerAjeno">
        Solo puede consultar sus propios datos (DNI {{ dniUsuario || '—' }}).
      </span>
    </v-alert>

    <section class="as-card">
      <div class="as-card__head">
        <div>
          <h2>Buscar colaborador</h2>
          <p>Escriba para filtrar y haga clic en la opción para ver el legajo.</p>
        </div>
        <span class="as-badge-secure">
          <v-icon size="14" color="#1d4ed8">mdi-shield-account-outline</v-icon>
          Solo lectura
        </span>
      </div>

      <div class="as-filters as-filters--select">
        <v-autocomplete
          v-model="seleccionId"
          :items="opcionesPersonal"
          :search-input.sync="busqueda"
          :filter="filtrarPersonal"
          item-text="label"
          item-value="id"
          label="Personal"
          placeholder="Buscar por DNI, código o apellidos…"
          outlined
          dense
          clearable
          hide-details
          background-color="white"
          prepend-inner-icon="mdi-magnify"
          :loading="loading"
          :disabled="!puedeVerAjeno && !!dniUsuario"
          :readonly="!puedeVerAjeno"
          no-data-text="Sin coincidencias"
          @change="onSeleccionar"
        >
          <template v-slot:item="{ item }">
            <div class="as-option">
              <strong>{{ item.apellidosNombres || '—' }}</strong>
              <span>
                DNI {{ item.dniCe || '—' }}
                <template v-if="item.codigoEmpleado"> · Cód. {{ item.codigoEmpleado }}</template>
                <template v-if="item.cargo || item.area"> · {{ item.cargo || item.area }}</template>
              </span>
            </div>
          </template>
          <template v-slot:selection="{ item }">
            <span class="as-selection">
              {{ item.apellidosNombres || '—' }}
              <small>({{ item.dniCe || item.codigoEmpleado || '—' }})</small>
            </span>
          </template>
        </v-autocomplete>
      </div>

      <div v-if="buscado && !loading && !resultado && !errorMsg" class="as-empty">
        <v-icon size="48" color="#94a3b8">mdi-account-off-outline</v-icon>
        <p>Seleccione un colaborador de la lista.</p>
      </div>

      <v-alert v-if="errorMsg" type="warning" dense outlined class="mb-0">
        {{ errorMsg }}
      </v-alert>

      <div v-if="resultado" class="as-consulta-resultado">
        <span v-if="esPropio" class="as-chip as-chip--ok mb-3">
          <v-icon left small color="#15803d">mdi-account-check-outline</v-icon>
          Mis datos
        </span>
        <legajo-detalle-lectura :item="resultado" :perfil-slug="perfilActivo" :titulo="false" />
      </div>
    </section>
  </div>
</template>

<script>
import AsPageHeader from '../components/AsPageHeader.vue'
import {
  cpList,
  LEGAJO_ENDPOINT,
  resolveClinicaUsuario,
  asegurarPerfilLegajo,
} from '../legajoApi'
import {
  normalizarLegajoRrhh,
  resolverPerfilLegajo,
  configPerfilLegajo,
  puedeVerLegajoAjeno,
  puedeConsultarRegistro,
  esMiLegajo,
  getDniUsuarioSesion,
} from '../rrhhLegajo'
import LegajoDetalleLectura from '../LegajoDetalleLectura.vue'

export default {
  name: 'RrhhLegajoConsulta',
  components: { AsPageHeader, LegajoDetalleLectura },
  data() {
    return {
      busqueda: '',
      seleccionId: null,
      loading: false,
      buscado: false,
      resultado: null,
      errorMsg: '',
      items: [],
      clinicaNombre: '',
      perfilActivo: resolverPerfilLegajo(),
      dniUsuario: getDniUsuarioSesion(),
    }
  },
  computed: {
    perfilLabel() {
      return (configPerfilLegajo(this.perfilActivo) || {}).label || this.perfilActivo
    },
    puedeVerAjeno() {
      return puedeVerLegajoAjeno(this.perfilActivo)
    },
    esPropio() {
      return this.resultado && esMiLegajo(this.resultado)
    },
    opcionesPersonal() {
      return (this.items || []).map((item) => ({
        ...item,
        label: [
          item.apellidosNombres || '',
          item.dniCe ? `DNI ${item.dniCe}` : '',
          item.codigoEmpleado ? `Cód. ${item.codigoEmpleado}` : '',
        ].filter(Boolean).join(' · '),
      }))
    },
  },
  async created() {
    this.perfilActivo = await asegurarPerfilLegajo()
    await this.resolverClinica()
    await this.cargarBase()
    if (this.dniUsuario) {
      const propio = (this.items || []).find(
        i => String(i.dniCe || '').replace(/\D/g, '') === String(this.dniUsuario).replace(/\D/g, ''),
      )
      if (propio && propio.id != null) {
        this.seleccionId = propio.id
        this.onSeleccionar(propio.id)
      }
    }
  },
  methods: {
    async resolverClinica() {
      try {
        const res = await resolveClinicaUsuario()
        if (res.ok) {
          this.clinicaNombre = res.clinica.nombre || res.clinica.descripCas || ''
        }
      } catch (e) {
        /* ignore */
      }
    },
    async cargarBase() {
      this.loading = true
      this.errorMsg = ''
      try {
        const params = {}
        if (this.clinicaNombre) params.clinica = this.clinicaNombre
        let lista = await cpList(LEGAJO_ENDPOINT, params)
        if (!this.puedeVerAjeno && this.dniUsuario) {
          const dni = String(this.dniUsuario).replace(/\D/g, '')
          lista = (lista || []).filter(
            i => String(i.dniCe || '').replace(/\D/g, '') === dni,
          )
        }
        this.items = lista || []
      } catch (e) {
        console.error(e)
        this.items = []
        this.errorMsg = 'Error al cargar personal. Intente nuevamente.'
      } finally {
        this.loading = false
      }
    },
    filtrarPersonal(item, queryText) {
      const q = String(queryText || '').trim().toLowerCase()
      if (!q) return true
      const qNorm = q.replace(/\s+/g, ' ')
      const qDni = q.replace(/\D/g, '')
      const qCodigo = q.replace(/\s+/g, '')
      const dni = String(item.dniCe || '').replace(/\D/g, '')
      const nom = String(item.apellidosNombres || '').toLowerCase()
      const codigo = String(item.codigoEmpleado || '').toLowerCase().replace(/\s+/g, '')
      if (qDni.length >= 3 && dni.includes(qDni)) return true
      if (codigo && (codigo.includes(qCodigo) || qCodigo.includes(codigo))) return true
      if (nom.includes(qNorm)) return true
      const palabras = qNorm.split(' ').filter(Boolean)
      if (palabras.length > 1 && palabras.every(p => nom.includes(p))) return true
      return false
    },
    onSeleccionar(id) {
      this.buscado = true
      this.resultado = null
      this.errorMsg = ''
      if (id == null || id === '') {
        this.seleccionId = null
        return
      }
      const elegido = (this.items || []).find(i => String(i.id) === String(id))
      if (!elegido) {
        this.errorMsg = 'No se encontró el personal seleccionado.'
        return
      }
      if (!puedeConsultarRegistro(elegido, this.perfilActivo)) {
        this.errorMsg = 'Solo puede consultar su propio legajo. Verifique que el DNI coincida con su sesión.'
        this.seleccionId = null
        return
      }
      this.resultado = normalizarLegajoRrhh(elegido)
    },
  },
}
</script>

<style scoped>
@import '../rrhh-as.css';
.as-filters--select {
  display: block;
}
.as-filters--select >>> .v-autocomplete {
  max-width: 100%;
}
.as-option {
  display: flex;
  flex-direction: column;
  padding: 4px 0;
  line-height: 1.25;
}
.as-option strong {
  font-size: 0.9rem;
  color: #0f172a;
}
.as-option span {
  font-size: 0.78rem;
  color: #64748b;
}
.as-selection small {
  color: #64748b;
  margin-left: 0.35rem;
}
.as-consulta-resultado {
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid var(--border);
}
.mb-3 { margin-bottom: 0.75rem; }
</style>
